import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
  ChevronLeft,
  Save,
  User,
  Mail,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
  Car,
  CreditCard,
  MapPin,
  Hash,
  Users,
} from "lucide-react";

const AutoInsuranceForm = () => {
  const { user } = useUserAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [formData, setFormData] = useState({
    // Datos del Contratante
    rfc: "",
    correoElectronico: "",
    nombre: "",
    rl: "",
    domicilio: "",
    // Datos del Carro
    vehiculoDescripcion: "",
    modelo: "",
    serie: "",
    numeroOcupantes: "",
    uso: "",
    servicio: "",
    cobertura: "",
    notas: "",
  });

  const usoOptions = [
    "Particular",
    "Comercial",
    "Público",
    "Industrial",
    "Agrícola",
  ];

  const servicioOptions = [
    "Nacional",
    "Internacional",
    "Ambos",
  ];

  const coberturaOptions = [
    "Responsabilidad Civil",
    "Daños Materiales",
    "Robo Total",
    "Gastos Médicos",
    "Cobertura Amplia",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.rfc.trim()) {
      return "El RFC es requerido";
    }
    if (!formData.correoElectronico.trim()) {
      return "El correo electrónico es requerido";
    }
    if (!formData.nombre.trim()) {
      return "El nombre es requerido";
    }
    if (!formData.domicilio.trim()) {
      return "El domicilio es requerido";
    }
    if (!formData.vehiculoDescripcion.trim()) {
      return "La descripción del vehículo es requerida";
    }
    if (!formData.modelo.trim()) {
      return "El modelo es requerido";
    }
    if (!formData.serie.trim()) {
      return "La serie es requerida";
    }
    if (!formData.numeroOcupantes) {
      return "El número de ocupantes es requerido";
    }
    if (!formData.uso) {
      return "El uso del vehículo es requerido";
    }
    if (!formData.servicio) {
      return "El servicio es requerido";
    }
    if (!formData.cobertura) {
      return "La cobertura es requerida";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setMessage({ text: validationError, type: "error" });
      return;
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      // Guardar solicitud en Firestore
      const policyData = {
        ...formData,
        userId: user.uid,
        userEmail: user.email,
        tipoPoliza: "seguro_auto",
        estado: "pendiente",
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await addDoc(collection(db, "policies"), policyData);

      setMessage({
        text: "¡Solicitud enviada exitosamente! Te contactaremos pronto.",
        type: "success",
      });

      // Limpiar formulario
      setTimeout(() => {
        navigate("/polizas");
      }, 2000);
    } catch (error) {
      console.error("Error al enviar solicitud:", error);
      setMessage({
        text: "Error al enviar la solicitud. Por favor, intenta nuevamente.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-slate-200">
          <AlertCircle className="w-16 h-16 text-[#096365] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Acceso no autorizado
          </h2>
          <p className="text-[#096365]">Por favor inicia sesión para continuar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-20 sm:pb-8">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-lg border-b border-slate-200/50 sticky top-16 z-30 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/polizas")}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-gradient-to-r from-[#11653a] to-[#096365] rounded-lg shadow-md">
                <Car className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-[#11653a] to-[#096365] bg-clip-text text-transparent">
                  Seguro de Auto
                </h1>
                <p className="text-xs text-slate-600">
                  Completa el formulario para solicitar tu seguro
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Message Alert */}
        {message.text && (
          <div
            className={`mb-8 p-4 rounded-xl border-2 flex items-center gap-3 ${
              message.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="font-medium">{message.text}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-12">
          {/* Datos del Contratante */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg">
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Datos del Contratante
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  RFC <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="rfc"
                    value={formData.rfc}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white uppercase"
                    placeholder="ABCD123456EF7"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Correo Electrónico <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    name="correoElectronico"
                    value={formData.correoElectronico}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    placeholder="correo@ejemplo.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    placeholder="Nombre completo"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  RL (Razón Social)
                </label>
                <div className="relative">
                  <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="rl"
                    value={formData.rl}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    placeholder="Razón social (opcional)"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Domicilio <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                  <textarea
                    name="domicilio"
                    value={formData.domicilio}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white resize-none"
                    placeholder="Calle, número, colonia, ciudad, estado, CP"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Datos del Carro */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#11653a] to-[#096365] rounded-lg">
                <Car className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Datos del Carro
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Vehículo y Descripción <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Car className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                  <textarea
                    name="vehiculoDescripcion"
                    value={formData.vehiculoDescripcion}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white resize-none"
                    placeholder="Ej: Nissan Sentra 2020, color blanco, 4 puertas"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Modelo <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Car className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="modelo"
                    value={formData.modelo}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    placeholder="Ej: 2020"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Serie <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="serie"
                    value={formData.serie}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white uppercase"
                    placeholder="Número de serie del vehículo"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  No. Ocupantes <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="number"
                    name="numeroOcupantes"
                    value={formData.numeroOcupantes}
                    onChange={handleInputChange}
                    min="1"
                    max="20"
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    placeholder="Ej: 5"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Uso <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Car className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    name="uso"
                    value={formData.uso}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white appearance-none"
                    required
                  >
                    <option value="">Selecciona el uso</option>
                    {usoOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Servicio <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    name="servicio"
                    value={formData.servicio}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white appearance-none"
                    required
                  >
                    <option value="">Selecciona el servicio</option>
                    {servicioOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Cobertura <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {coberturaOptions.map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-3 p-4 border-2 border-slate-300 rounded-xl cursor-pointer hover:border-[#5fc500] hover:bg-green-50 transition-all"
                    >
                      <input
                        type="radio"
                        name="cobertura"
                        value={option}
                        checked={formData.cobertura === option}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-[#5fc500] border-2 border-slate-300 focus:ring-2 focus:ring-[#5fc500]"
                        required
                      />
                      <span className="text-sm font-medium text-slate-700">
                        {option}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Notas Adicionales */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Notas Adicionales
              </h2>
            </div>

            <textarea
              name="notas"
              value={formData.notas}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white resize-none"
              placeholder="Información adicional que consideres relevante..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={() => navigate("/polizas")}
              className="flex-1 px-6 py-4 border-2 border-slate-300 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-[#11653a] to-[#096365] rounded-xl font-bold text-white shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enviando solicitud...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Enviar Solicitud
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutoInsuranceForm;

